import { DataTable } from "@/components/datatable/datatable"
import { DataTableSupabase } from "@/components/datatable/supadatatable"
import { ToolbarItem } from "@/components/datatable/toolbaritem"
import { useTableController } from "@/components/datatable/controller"
import { Page, PageTitle } from "@/components/page"
import { useDatabaseData, usePage } from "@/components/page/hooks"
import { Button } from "@/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { supabase } from "@/lib/data"
import { Plus, Trash2 } from "lucide-react"
import { usePermissions } from "@/lib/hooks/usePermissions"
import { useEffect, useState } from "react"

const appliesMapping = {
  user_message: "User",
  agent_response: "Agent",
  conversation: "Conversation",
  all: "All"
}

const columns: Columns<Evaluation_Item> = {
  id: { header: "id", size: 100 },
  name: { header: "Name", size: 300 },
  key_signals: { header: "Key Signals", size: 400 },
  type: { header: "Applies To", size: 300, cell: ({ row }) => appliesMapping[row.original.type] },
  description: { header: "Description", size: 400 },
  category: { header: "Category", size: 200, cell: ({ row }) => row && row.original && row.original.category ? row?.original?.category["name"] : "" },
  weight: { header: "Weight", size: 120 },
  created_at: { header: "Created", cell: DataTable.cellRender.date, size: 166 },
  askhuman: { header: "Ask Human", size: 120, cell: ({ row }) => (row.original.askhuman ? "Yes" : "No") },
  highrisk: { header: "High Risk", size: 120, cell: ({ row }) => (row.original.highrisk ? "Yes" : "No") },
  lowconf: { header: "Low Confidence", size: 120, cell: ({ row }) => (row.original.lowconf ? "Yes" : "No") },
}

export function Eval_Items() {

  const { navigate, team } = usePage()
  const { canCreate, canDelete } = usePermissions()
  const { data: categories } = useDatabaseData("eval_categories", { fields: ["id", "name"], filter: q => q.eq("team", team?.id) })
  const [categoryFilter, setCategoryFilter] = useState<number | null>(null)
  const ctrl = useTableController()

  const filterLabel = categories?.find((c: any) => c.id === categoryFilter)?.name || "<All>"

  useEffect(() => { ctrl.reload() }, [categoryFilter])


  const menu: DropdownMenuContents = [
    ...(canDelete("riskcategories") ? [{
      title: "Delete", action: async (v) => {
        await supabase.from("eval_items").delete().eq("id", v.id)
      },
      icon: <Trash2 />,
      ask: "Are you sure you want to delete this evaluation item?",
    }] : []),
  ]

  return <Page>
    <div className="h-full grid grid-rows-[auto_1fr] gap-4">
      <div className="flex">
        <PageTitle />
        <div className="grow"></div>
        {canCreate("riskcategories") && <div>
          <Button className="rounded-lg" onClick={() => navigate('/evaluations/evalitems/new')}><Plus className="h-4 w-4" />New Item</Button>
        </div>}
      </div>
      <DataTableSupabase
        table="eval_items"
        columns={columns}
        showSelection
        onRowClick={"/evaluations/evalitems"}
        sort={["created_at", "desc"]}
        actions={menu.length > 0 ? menu : undefined}
        controller={ctrl}
        filter={q => { q.eq("team", team?.id); if (categoryFilter != null) q.eq("category", categoryFilter); return q }}
        select={`
        *,
        category (
          name
        )
      `}
        enableServerSideFiltering={true}
        searchableColumns={["name", "description"]}
      >
        <ToolbarItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <span className="text-sm cursor-pointer hover:text-blue-500 whitespace-nowrap">Filter by {filterLabel}</span>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setCategoryFilter(null)}>&lt;All&gt;</DropdownMenuItem>
              {categories?.map((c: any) => (
                <DropdownMenuItem key={c.id} onClick={() => setCategoryFilter(c.id)}>{c.name}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </ToolbarItem>
      </DataTableSupabase>
    </div>
  </Page>

}
